import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseKey);

export const MAX_DASHBOARD_DAYS = 7;
export const MAX_CSV_DAYS = 92; // ~3 months

const PRICE_COLS = 'dispatch_interval,trading_day,energy,contingency_raise,contingency_lower,regulation_raise,regulation_lower,rocof';
const SCHEDULE_COLS = 'dispatch_interval,trading_day,facility_code,market_service,quantity';
const PAGE_SIZE = 1000;

async function fetchAllPages(table, cols, startDate, endDate, extraFilters = {}) {
  let allRows = [];
  let from = 0;
  while (true) {
    let query = supabase
      .from(table)
      .select(cols)
      .gte('trading_day', startDate)
      .lte('trading_day', endDate)
      .eq('superseded', false)
      .order('dispatch_interval', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    for (const [key, val] of Object.entries(extraFilters)) {
      if (!val) continue;
      // market_service is an exact match, facility_code is a fuzzy search
      if (key === 'market_service') {
        query = query.eq(key, val);
      } else {
        query = query.ilike(key, `%${val}%`);
      }
    }

    const { data, error } = await query;
    if (error) throw error;
    allRows = allRows.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return allRows;
}

export async function fetchPrices(startDate, endDate) {
  return fetchAllPages('dispatch_prices', PRICE_COLS, startDate, endDate);
}

export async function fetchSchedule(startDate, endDate, facilityFilter = '', marketService = '') {
  const filters = {};
  if (facilityFilter) filters.facility_code = facilityFilter;
  if (marketService) filters.market_service = marketService;
  return fetchAllPages('dispatch_schedule', SCHEDULE_COLS, startDate, endDate, filters);
}

export async function fetchFacilities(startDate, endDate) {
  const { data, error } = await supabase
    .from('dispatch_schedule')
    .select('facility_code')
    .gte('trading_day', startDate)
    .lte('trading_day', endDate)
    .eq('superseded', false);
  if (error) throw error;
  return [...new Set(data.map(r => r.facility_code))].sort();
}

export function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

export async function fetchLastUpdated() {
  const { data, error } = await supabase
    .from('dispatch_prices')
    .select('created_at')
    .eq('superseded', false)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0]?.created_at || null;
}

export async function fetchMostRecentDate() {
  const { data, error } = await supabase
    .from('dispatch_prices')
    .select('trading_day')
    .eq('superseded', false)
    .order('trading_day', { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0]?.trading_day || getYesterday();
}
